import { useCallback } from 'react';

import useCreateDocumentByOneTimeLink from '@/modules/transaction/components/TransactionDetails/OneTimeLinkUpload/queries/useCreateDocumentByOneTimeLink.query';
import useGetScannedDocumentByHashIdQuery from '@/modules/transaction/components/TransactionDetails/OneTimeLinkUpload/queries/useGetScannedDocumentByHashId.query';
import useOneTimeLinkStore from '@/modules/transaction/components/TransactionDetails/OneTimeLinkUpload/store/useOneTimeLink.store';
import useUploadFileToS3Query from '@/modules/transaction/queries/useUploadFileToS3.query';

import useCompleteFileUploadByLinkQuery, { UploadedFileResponse } from './useCompleteFileUploadByLink.query';
import useCreateFileUploadUrlByLinkQuery from './useCreateFileUploadUrlByLink.query';

const useUploadFileByLink = () => {
  const oneTimeLink = useOneTimeLinkStore((state) => state.state);
  const { createDocumentByLink } = useCreateDocumentByOneTimeLink();
  const { getScannedDocument } = useGetScannedDocumentByHashIdQuery();
  const { createFileUploadUrlLink } = useCreateFileUploadUrlByLinkQuery();
  const { uploadFileToS3 } = useUploadFileToS3Query();
  const { completeFileUploadByLink } = useCompleteFileUploadByLinkQuery();

  const handleCreateDocument = useCallback(async () => {
    const createdDocument = await createDocumentByLink({ 
      employeeId: oneTimeLink.employeeId || '',
      hashId: oneTimeLink.hashId || '',
      organizationId: oneTimeLink.organizationId || '',
      organizationPath: oneTimeLink.organizationPath || '',
    });
    return createdDocument;
  }, [oneTimeLink, createDocumentByLink]);

  const handleImportFile = useCallback(async (documentId: string, formData: Blob, fileName: string) => {
    const uploadUrl = await createFileUploadUrlLink({
      documentId,
      employeeId: oneTimeLink.employeeId,
      hashId: oneTimeLink.hashId,
      name: fileName,
    });

    if (!uploadUrl?.data?.upload_url) {
      return;
    }
  
    await uploadFileToS3({ fileBlob: formData, upload_url: uploadUrl?.data?.upload_url });

    const importedFile = await completeFileUploadByLink({
      documentId,
      employeeId: oneTimeLink.employeeId,
      hashId: oneTimeLink.hashId,
      objectKey: uploadUrl?.data?.object_key,
      fileName,
    });
    return importedFile;
  }, [oneTimeLink, createFileUploadUrlLink, uploadFileToS3, completeFileUploadByLink]);

  const handleUploadFile = useCallback(async (formData: Blob, fileName: string) => {
    if (oneTimeLink.documentId) {
      const importedFile = await handleImportFile(oneTimeLink.documentId, formData, fileName);
      return { ...importedFile, documentId: oneTimeLink.documentId };
    } else {
      const createdDocument = await handleCreateDocument();
      const documentId = `${createdDocument?.document_id}`;
      const importedFile = await handleImportFile(documentId, formData, fileName);
      return { ...importedFile, documentId };
    }
  }, [handleImportFile, handleCreateDocument, oneTimeLink]);

  const handleScanFile = useCallback(async (uploadedFile: UploadedFileResponse, hashId: string) => {
    if (!uploadedFile?.id) {
      return;
    }
    const scannedFile = await getScannedDocument(`${uploadedFile.id}`, hashId);
    return scannedFile;
  }, [getScannedDocument]);

  return {
    handleUploadFile,
    handleScanFile,
  };
};

export default useUploadFileByLink;
